import type { MessageKey } from '@bitwize/i18n-messages';

import { useI18n } from '../../../shared/i18n';
import { Button, Field, Input, Select } from '../../../shared/ui';
import type { AttributionType, ProjectFilterOptions, ProjectSort } from '../publicApi';

export interface ProjectFilterValue {
  q?: string | undefined;
  attribution?: AttributionType | undefined;
  sort?: ProjectSort | undefined;
  yearFrom?: number | undefined;
  yearTo?: number | undefined;
}

export interface ProjectFilterBarProps {
  options: ProjectFilterOptions;
  value: ProjectFilterValue;
  /** The listing URL this form submits to — locale-prefixed, no query string. */
  action: string;
}

/**
 * Human labels for the sort keys the API is expected to offer. Keyed by the
 * wire value rather than assumed — `options.sorts` is what actually renders,
 * so a key this map does not recognise still renders (as its own raw value)
 * rather than disappearing from the list.
 */
const SORT_LABEL_KEYS: Readonly<Record<string, MessageKey>> = {
  default: 'common.portfolio.sort.default',
  '-published_at': 'common.portfolio.sort.newest',
  published_at: 'common.portfolio.sort.oldest',
  '-year': 'common.portfolio.sort.yearNewest',
  year: 'common.portfolio.sort.yearOldest',
  title: 'common.portfolio.sort.title',
};

/** Whether any field carries a non-default value — governs the visibility of
 * the "Clear all" affordance. */
function hasValue(value: ProjectFilterValue): boolean {
  return Boolean(
    value.q ||
    value.attribution ||
    value.yearFrom ||
    value.yearTo ||
    (value.sort && value.sort !== 'default'),
  );
}

/**
 * The portfolio's filters.
 *
 * A plain `GET` form: page one of a filtered listing is a URL, and building
 * that URL is what a browser already does with a form and no JavaScript
 * (FR-PORT — the acceptance criterion this component exists to satisfy).
 * Every control has a `name`, submitting reloads the page with the new query
 * string, and `Pagination` picks the rest up from there.
 *
 * Options — the year bounds, the sorts, the attribution values and their
 * labels — come from `GET /projects/filters` rather than being hard-coded,
 * so the filter UI cannot drift from the enum or claim a year nothing was
 * published in.
 */
export function ProjectFilterBar({ options, value, action }: ProjectFilterBarProps) {
  const { t } = useI18n();
  const active = hasValue(value);

  return (
    <form
      method="get"
      action={action}
      className="hs-card flex flex-col gap-5 rounded-xl p-4 sm:p-5"
    >
      <Field label={t('common.portfolio.searchLabel')}>
        {({ id }) => (
          <Input
            id={id}
            type="search"
            name="q"
            defaultValue={value.q}
            placeholder={t('common.portfolio.searchPlaceholder')}
          />
        )}
      </Field>

      {/*
        Everything below the search box is a refinement, not the primary
        action — set off by a hairline rather than folded into the same row,
        so the eye reads "search, then narrow" instead of five equal fields.
      */}
      <div className="flex flex-col gap-4 border-t border-border pt-5 sm:flex-row sm:flex-wrap sm:items-end">
        <Field label={t('common.portfolio.attributionLabel')} className="sm:w-44">
          {({ id }) => (
            <Select id={id} name="attribution" defaultValue={value.attribution ?? ''}>
              <option value="">{t('common.portfolio.anyAttribution')}</option>
              {options.attributions.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={t('common.portfolio.sortLabel')} className="sm:w-44">
          {({ id }) => (
            <Select id={id} name="sort" defaultValue={value.sort ?? 'default'}>
              {options.sorts.map((sort) => {
                const labelKey = SORT_LABEL_KEYS[sort];
                return (
                  <option key={sort} value={sort}>
                    {labelKey ? t(labelKey) : sort}
                  </option>
                );
              })}
            </Select>
          )}
        </Field>

        <Field label={t('common.portfolio.yearLabel')}>
          {({ id }) => (
            <div className="flex items-center gap-2">
              <Input
                id={id}
                type="number"
                name="year_from"
                inputMode="numeric"
                min={options.year_from}
                max={options.year_to}
                defaultValue={value.yearFrom}
                aria-label={t('common.portfolio.yearFrom')}
                className="w-20"
              />
              <span aria-hidden="true" className="text-text-subtle">
                –
              </span>
              <Input
                type="number"
                name="year_to"
                inputMode="numeric"
                min={options.year_from}
                max={options.year_to}
                defaultValue={value.yearTo}
                aria-label={t('common.portfolio.yearTo')}
                className="w-20"
              />
            </div>
          )}
        </Field>

        {/*
          Pushed to the far end on a wide screen (the inline end, so it lands
          on the left in Arabic) and full-width on mobile, where cramming a
          link and a button onto one row is the thing that reads as messy.
        */}
        <div className="flex items-center gap-4 sm:ms-auto">
          {active ? (
            <a
              href={action}
              className="text-sm font-medium text-text-muted transition-colors hover:text-accent"
            >
              {t('common.actions.clearAll')}
            </a>
          ) : null}
          <Button type="submit" className="w-full sm:w-auto">
            {t('common.portfolio.applyFilters')}
          </Button>
        </div>
      </div>
    </form>
  );
}
